// Public share-link service. Creates a public read-only document under
// `shares/{shareId}` that anyone with the link can view. Tracks viewCount
// + lastViewedAt so the owner can tell if recruiters opened it.
import { db } from '../firebase.js';
import {
    addDoc, collection, doc, getDoc, query, where, orderBy, getDocs,
    deleteDoc, updateDoc, increment, Timestamp, serverTimestamp
} from 'firebase/firestore';

const SHARES = 'shares';

// Lightweight SHA-256 (used for optional password protection). The hash
// is *not* a security boundary — the share ID is. Treat it as a basic
// gate against drive-by URL access, not real auth.
const sha256 = async (text) => {
    const enc = new TextEncoder().encode(text);
    const buf = await crypto.subtle.digest('SHA-256', enc);
    return Array.from(new Uint8Array(buf))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
};

export const createShare = async (uid, resumeData, template, options = {}) => {
    const { password, title } = options;
    const passwordHash = password ? await sha256(password) : null;

    const ref = await addDoc(collection(db, SHARES), {
        ownerUid: uid,
        title: title || resumeData.personal?.fullName || 'Resume',
        resumeData: JSON.stringify(resumeData),
        template: template || 'modern',
        viewCount: 0,
        lastViewedAt: null,
        passwordHash,
        createdAt: Timestamp.now()
    });
    return ref.id;
};

// Fetches a shared resume. If passwordHash is set on the doc, the caller
// must pass the matching plaintext.
export const getShare = async (shareId, password) => {
    const ref = doc(db, SHARES, shareId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Share not found.');

    const data = snap.data();
    if (data.passwordHash) {
        if (!password) {
            const err = new Error('Password required.');
            err.code = 'password-required';
            throw err;
        }
        const incoming = await sha256(password);
        if (incoming !== data.passwordHash) {
            const err = new Error('Incorrect password.');
            err.code = 'password-incorrect';
            throw err;
        }
    }

    return {
        id: snap.id,
        resumeData: JSON.parse(data.resumeData),
        template: data.template || 'modern',
        title: data.title,
        viewCount: data.viewCount || 0,
        ownerUid: data.ownerUid
    };
};

// Best-effort — failures are swallowed so a flaky network doesn't ruin
// the viewer experience.
export const incrementShareView = async (shareId) => {
    try {
        await updateDoc(doc(db, SHARES, shareId), {
            viewCount: increment(1),
            lastViewedAt: serverTimestamp()
        });
    } catch (e) {
        console.warn('Share view counter increment failed:', e.message);
    }
};

export const listMyShares = async (uid) => {
    const q = query(
        collection(db, SHARES),
        where('ownerUid', '==', uid),
        orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => {
        const data = d.data();
        return {
            id: d.id,
            title: data.title,
            template: data.template,
            viewCount: data.viewCount || 0,
            lastViewedAt: data.lastViewedAt?.toDate?.() || null,
            createdAt: data.createdAt?.toDate?.() || null,
            hasPassword: !!data.passwordHash
        };
    });
};

export const deleteShare = async (shareId) => {
    await deleteDoc(doc(db, SHARES, shareId));
};
